import axios from "axios";
import { useEffect, useState } from "react";
import { passengerHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { ModuleConfig, ModuleFields } from "~/Utils/drilldownData";

// Define the option type for select fields
export interface OptionType {
  label: string;
  value: string;
}

// Create a type for fields that may have async options
export type AsyncSelectField = ModuleFields & {
  loadOptions?: () => Promise<OptionType[]>;
};

// Function to fetch data and transform to options
export const FetchSubModule = async (
  value?: string,
  currentAirport?: string
): Promise<OptionType[]> => {
  try {
    console.log("fetchsubModule", { value, currentAirport });
    const matchAirport = (item: any) => {
    const airport = currentAirport?.toLowerCase() || "";

      if (airport === "all") return true;

      const airports = item.airports || item.airport || "";

      return airports
        ?.split(",")
        .map((a: string) => a.trim().toLowerCase())
        .includes(airport);
    };

    // --- CASE 1: Airport = ALL (merge both API responses) ---
    if (currentAirport?.toLowerCase() === "all" && !value) {
      const [parentRes, publicRes] = await Promise.all([
        axios.get(`${passengerHost}/api/zonelevel-parent`),
        axios.get(`${passengerHost}/api/zonelevel-public`)
      ]);

      const parentData = parentRes?.data || [];
      const publicData = publicRes?.data || [];

      // Apply your existing filtering rules
      const parentOptions = parentData
        .filter((item: any) =>
          item.published === true &&
          !["public", "passenger"].includes(item.name?.toLowerCase()) &&
          item.parent_term_id === ""
        )
        .map((item: any) => ({
          value: item.id,
          label: decodeHTML(item.name),
        }));

      const publicFiltered = publicData.filter((item: any) => {
        if (!item.published) return false;
        if (!value) return true;
        return item.parent_term_name?.toLowerCase() === value.toLowerCase();
      });

      const publicOptions = publicFiltered.map((item: any) => ({
        value: item.id,
        label: decodeHTML(item.name),
      }));

      return [...parentOptions, ...publicOptions];
    }

    // --- CASE 2: Airport = KLIA1 / KLIA2 (use /public API) ---
    if (["klia1", "klia2"].includes(currentAirport?.toLowerCase() || "") && value) {
      const response = await axios.get(`${passengerHost}/api/zonelevel-public`);
      const data = response?.data || [];

      const filteredData = data.filter((item: any) => {
        if (!item.published) return false;
        if (!matchAirport(item)) return false;
        if (!value) return true;
        return item.parent_term_name?.toLowerCase() === value.toLowerCase();
      });

      return filteredData.map((item: any) => ({
        value: item.id,
        label: decodeHTML(item.name),
      }));
    }

    // --- CASE 3: Any other airport → use /parent API ---
    if(!["klia1", "klia2"].includes(currentAirport?.toLowerCase() || "")) {
      const response = await axios.get(`${passengerHost}/api/zonelevel-parent`);
      const data = response?.data || [];
      const options = data
        .filter((item: any) =>
          item.published === true &&
          !["public", "passenger"].includes(item.name?.toLowerCase()) &&
          item.parent_term_id === "" &&
          matchAirport(item)
        )
        .map((item: any) => ({
          value: item.id,
          label: decodeHTML(item.name),
        }));
      return options;
    }

  } catch (error) {
    console.error("Failed to fetch zone options:", error);
    return [];
  }
};
